/**
 * @file hooks/useAnalytics.ts
 * @description 統計分析 TanStack Query hooks（M4）。
 *
 * 提供以下 hooks：
 * - useOverview()            — 頂層 KPI 摘要
 * - useCaseTrend()           — 案件趨勢
 * - useAuditorStats()        — 稽核員統計
 * - useCertificateTrend()    — 發證趨勢
 * - useClientStats()         — 客戶統計
 * - useFinancialSummary()    — 財務摘要
 *
 * 每個 hook 回傳 TanStack Query 的 UseQueryResult。
 * 快取時間設為 5 分鐘（統計數據更新頻率低）。
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type {
  AuditorStatsResponse,
  CaseGroupBy,
  CaseTrendResponse,
  CertTrendResponse,
  ClientStatsResponse,
  FinancialSummaryResponse,
  OverviewData,
} from '@/lib/analytics-types';

/** 統計分析 query 快取時間（毫秒） */
const ANALYTICS_STALE_TIME = 5 * 60 * 1000; // 5 分鐘

/** 日期範圍參數 */
export interface DateRangeParams {
  from?: string; // YYYY-MM-DD
  to?: string; // YYYY-MM-DD
}

/**
 * 建立 URL query string
 */
function buildQuery(params: Record<string, string | undefined>): string {
  const pairs = Object.entries(params)
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => `${k}=${encodeURIComponent(v!)}`)
    .join('&');
  return pairs ? `?${pairs}` : '';
}

/**
 * 頂層 KPI 概覽 hook。
 *
 * 資料包括：
 * - 待處理案件數
 * - 進行中稽核任務數
 * - 本月新發證書數
 * - 即將到期（30天內）證書數
 * - SLA 逾期案件數
 * - 總客戶數
 * - 有效證書數
 */
export function useOverview() {
  return useQuery({
    queryKey: ['analytics', 'overview'],
    queryFn: () => apiClient.get<{ overview: OverviewData }>('/api/analytics/overview'),
    staleTime: ANALYTICS_STALE_TIME,
  });
}

/**
 * 案件趨勢 hook。
 *
 * @param range   日期範圍（from / to 均可選）
 * @param groupBy 分組方式：month / week / scheme
 */
export function useCaseTrend(range: DateRangeParams = {}, groupBy: CaseGroupBy = 'month') {
  const qs = buildQuery({ from: range.from, to: range.to, groupBy });
  return useQuery({
    queryKey: ['analytics', 'cases', range, groupBy],
    queryFn: () => apiClient.get<CaseTrendResponse>(`/api/analytics/cases${qs}`),
    staleTime: ANALYTICS_STALE_TIME,
  });
}

/**
 * 稽核員統計 hook。
 *
 * @param range 日期範圍（以 dispatchedAt 為基準）
 */
export function useAuditorStats(range: DateRangeParams = {}) {
  const qs = buildQuery({ from: range.from, to: range.to });
  return useQuery({
    queryKey: ['analytics', 'auditors', range],
    queryFn: () => apiClient.get<AuditorStatsResponse>(`/api/analytics/auditors${qs}`),
    staleTime: ANALYTICS_STALE_TIME,
  });
}

/**
 * 發證趨勢 hook。
 *
 * @param range 日期範圍（以 issuedAt 為基準）
 */
export function useCertificateTrend(range: DateRangeParams = {}) {
  const qs = buildQuery({ from: range.from, to: range.to });
  return useQuery({
    queryKey: ['analytics', 'certificates', range],
    queryFn: () => apiClient.get<CertTrendResponse>(`/api/analytics/certificates${qs}`),
    staleTime: ANALYTICS_STALE_TIME,
  });
}

/**
 * 客戶統計 hook。
 */
export function useClientStats() {
  return useQuery({
    queryKey: ['analytics', 'clients'],
    queryFn: () => apiClient.get<ClientStatsResponse>('/api/analytics/clients'),
    staleTime: ANALYTICS_STALE_TIME,
  });
}

/**
 * 財務摘要 hook。
 *
 * @param range 日期範圍
 */
export function useFinancialSummary(range: DateRangeParams = {}) {
  const qs = buildQuery({ from: range.from, to: range.to });
  return useQuery({
    queryKey: ['analytics', 'financial', range],
    queryFn: () => apiClient.get<FinancialSummaryResponse>(`/api/analytics/financial${qs}`),
    staleTime: ANALYTICS_STALE_TIME,
  });
}
